import { createContext, useContext, type ReactNode } from "react";
import type { AppConfig } from "./api.js";

/**
 * App-wide runtime config, fetched once from /api/config at boot (see
 * main.tsx). Edition (entra / dev / clerk) is a runtime property of the
 * container — the same image serves both b2c and enterprise — so the SPA
 * can't decide it at build time. Everything that branches on edition reads
 * it from here.
 */
const ConfigContext = createContext<AppConfig | null>(null);

export function ConfigProvider({
  config,
  children,
}: {
  config: AppConfig;
  children: ReactNode;
}) {
  return (
    <ConfigContext.Provider value={config}>{children}</ConfigContext.Provider>
  );
}

export function useConfig(): AppConfig {
  const ctx = useContext(ConfigContext);
  if (!ctx) throw new Error("useConfig must be used within <ConfigProvider>");
  return ctx;
}

/** True when running the b2c (Clerk) edition. One named place to branch on,
 *  rather than comparing `authMode` string literals across the SPA. */
export function isClerkMode(config: AppConfig): boolean {
  return config.authMode === "clerk";
}

/** The product's name as it appears in the browser tab: the b2c edition is
 *  "makerdoc.ai", the enterprise (and local dev) edition "MakerX Multiplayer". */
export function productName(config: AppConfig): string {
  return isClerkMode(config) ? "makerdoc.ai" : "MakerX Multiplayer";
}

/**
 * True when this edition has the WHOLE Brand Studio — identity, guidelines,
 * the brand book and the shared asset library — i.e. a per-team brand profile
 * exists to hang them off (b2c).
 *
 * The templates half needs no such check: every edition has a template
 * library. b2c scopes it to the caller's team; the single-org editions have
 * one instance-wide library that admins manage and everyone builds from. Use
 * this for the surfaces that genuinely aren't there, never as a proxy for
 * "is this b2c" on a templates screen.
 */
export function hasFullBrandStudio(config: AppConfig): boolean {
  return config.brandScope === "full";
}
